"use client";

import { useTranslations } from "next-intl";
import { type ReactNode } from "react";
import { BsCheckCircle, BsCheckCircleFill } from "react-icons/bs";
import { generateIcon } from "utils";
import { useAiFilterContext } from "../../context";
import { type AiFilterItemType } from "./types";

interface IAIFiltersSelectableWrapperProps {
  children: ReactNode;
  items: AiFilterItemType[];
  handleSaveFilters: VoidFunction;
  handlePayFilters?: VoidFunction;
  showLoader?: boolean;
  buttonLabel?: string;
  totalCost?: string;
  imagesToGenerate?: number;
  isButtonDisabled?: boolean;
  minImagesMessage?: string;
  locked?: boolean;
}

export default function AIFiltersSelectableWrapper({
  children,
  items,
  showLoader,
  handleSaveFilters,
  handlePayFilters,
  buttonLabel,
  totalCost,
  imagesToGenerate,
  isButtonDisabled,
  minImagesMessage,
  locked = false,
}: IAIFiltersSelectableWrapperProps) {
  const { selectedItems, toggleAll } = useAiFilterContext();
  const t = useTranslations();
  const getToggleAllButtonClassName = () => {
    const color =
      selectedItems.length === items.length && items.length > 0
        ? "text-purple-950"
        : "text-gray-900";

    return `cursor-pointer ${color}`;
  };

  if (items.length === 0) {
    return null;
  }
  return (
    <section>
      {!locked && (
      <div className="card bg-base-200/50 rounded-lg p-2 flex flex-row items-center">
        <div className="pl-1">
          <span
            className={getToggleAllButtonClassName()}
            onClick={() => {
              toggleAll(items);
            }}
          >
            {generateIcon(
              selectedItems.length > 0 ? BsCheckCircleFill : BsCheckCircle,
              "text-[16px] scale-[1.35]"
            )}
          </span>
        </div>

        {selectedItems.length > 0 ? (
          <div className="flex flex-col lg:flex-row gap-2 items-start lg:items-center ml-2 lg:ml-3 flex-1 min-w-0">
            <span className="text-gray-700 text-[12px] lg:text-[15px] font-semibold whitespace-nowrap">
              {t("Gallery.itemSelected", { selected: selectedItems.length })}
            </span>
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              <button
                type="button"
                onClick={() => handleSaveFilters()}
                className={`btn btn-sm lg:btn-md ${showLoader || isButtonDisabled ? "btn-disabled" : "btn-primary"}`}
                disabled={showLoader || isButtonDisabled}
              >
                <div className="flex flex-row items-center justify-center gap-2">
                  {showLoader ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : null}
                  <span className="text-xs lg:text-sm">{buttonLabel || t("Buttons.save")}</span>
                </div>
              </button>
              {handlePayFilters && (
                <button
                  type="button"
                  onClick={() => handlePayFilters()}
                  className="btn btn-sm lg:btn-md btn-success"
                >
                  {t("Buttons.pay")}
                </button>
              )}
              {totalCost && (
                <span className="text-sm lg:text-lg font-bold text-green-600 whitespace-nowrap">
                  {totalCost}
                </span>
              )}
              {imagesToGenerate !== undefined && (
                <span className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">
                  ({imagesToGenerate} {t("Gallery.imagesToGenerate")})
                </span>
              )}
            </div>
            {isButtonDisabled && minImagesMessage && (
              <span className="text-xs lg:text-sm text-red-500">
                {minImagesMessage}
              </span>
            )}
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-2 items-start lg:items-center ml-2 lg:ml-3 flex-1 min-w-0">
            <span className="text-gray-700 text-[12px] lg:text-[15px] font-semibold whitespace-nowrap">
              {t("Gallery.selectAll")}
            </span>
            <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
              <button
                type="button"
                onClick={() => handleSaveFilters()}
                className={`btn btn-sm lg:btn-md ${showLoader || isButtonDisabled ? "btn-disabled" : "btn-primary"}`}
                disabled={showLoader || isButtonDisabled}
              >
                <div className="flex flex-row items-center justify-center gap-2">
                  {showLoader ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : null}
                  <span className="text-xs lg:text-sm">{buttonLabel || t("Buttons.save")}</span>
                </div>
              </button>
              {totalCost && (
                <span className="text-sm lg:text-lg font-bold text-green-600 whitespace-nowrap">
                  {totalCost}
                </span>
              )}
              {imagesToGenerate !== undefined && (
                <span className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">
                  ({imagesToGenerate} {t("Gallery.imagesToGenerate")})
                </span>
              )}
            </div>
            {isButtonDisabled && minImagesMessage && (
              <span className="text-xs lg:text-sm text-red-500">
                {minImagesMessage}
              </span>
            )}
          </div>
        )}
      </div>
      )}

      <div className="relative transition-all pt-2 lg:pt-[30px]">
        {children}
      </div>
    </section>
  );
}